'use client';

import { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  InputAdornment,
  Paper,
  Stack,
} from '@mui/material';
import { Schedule, Delete, CalendarToday } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Chip } from "@mui/material";

const drawerWidth = 220;
const API_URL = process.env.REACT_APP_SCHEDULE_API_URL;

const BookSchedule = ({ isSidebarOpen, topbarHeight = 64 }) => {
  const [showForm, setShowForm] = useState(false);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(['']);
  const [scheduleData, setScheduleData] = useState([]);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/schedule`)
      .then((res) => res.json())
      .then((data) => {
        setScheduleData(data.success ? data.schedules : []);
      });
  }, []);

  const handleSlotChange = (index, value) => {
    const updatedSlots = [...slots];
    updatedSlots[index] = value;
    setSlots(updatedSlots);
  };

  const addSlot = () => setSlots([...slots, '']);
  const removeSlot = (index) => setSlots(slots.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!date) return alert("Please select a date");

    const url = editId
      ? `${API_URL}/schedule/${editId}`
      : `${API_URL}/schedule`;

    const method = editId ? "PUT" : "POST";

    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        date: dayjs(date).format("YYYY-MM-DD"),
        slots,
      }),
    });

    const result = await response.json();
    alert(result.message);

    const updated = await fetch(`${API_URL}/schedule`).then((res) =>
      res.json()
    );

    setScheduleData(updated.success ? updated.schedules : []);
    setShowForm(false);
    setDate(null);
    setSlots([""]);
    setEditId(null);
  };

  const handleDelete = async (id) => {
    const res = await fetch(`${API_URL}/schedule/${id}`, {
      method: 'DELETE',
    });
    const result = await res.json();
    alert(result.message);

    const updated = await fetch(`${API_URL}/schedule`).then((res) =>
      res.json()
    );
    setScheduleData(updated.success ? updated.schedules : []);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          p: 3,
          mt: `${topbarHeight + 16}px`,
          ml: isSidebarOpen ? `${drawerWidth}px` : 0,
          minHeight: `calc(100vh - ${topbarHeight + 16}px)`,
          transition: 'margin-left 0.3s',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Box display="flex" justifyContent="flex-end" mb={4}>
          <Button
            onClick={() => setShowForm(!showForm)}
            variant="contained"
            startIcon={<Schedule />}
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              width: 240,
              fontWeight: 600,
            }}
          >
            Add Schedule
          </Button>
        </Box>

        {showForm && (
          <Paper elevation={4} sx={{ maxWidth: 450, mx: 'auto', mb: 4, p: 3 }}>
            <Stack spacing={2}>
              <DatePicker
                label="Select Date"
                value={date}
                onChange={(newValue) => setDate(newValue)}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <CalendarToday />
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />

              {slots.map((slot, index) => (
                <TextField
                  key={index}
                  label={`Time Slot ${index + 1}`}
                  value={slot}
                  onChange={(e) => handleSlotChange(index, e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => removeSlot(index)}>
                          <Delete />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ))}

              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={addSlot} fullWidth>
                  Add Time Slot
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  fullWidth
                >
                  {editId ? "Update" : "Submit"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        <Grid container spacing={3}>
          {scheduleData.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ backgroundColor: '#003366', color: 'white', p: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'lightblue' }}>
                    {dayjs(item.date).format('MMMM D, YYYY')}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                    {item.time_slots.map((slot, idx) => (
                      <Chip
                        key={idx}
                        label={slot}
                        variant="outlined"
                        sx={{ color: "white" }}
                      />
                    ))}
                  </Stack>

                  <Button
                    onClick={() => handleDelete(item.id)}
                    variant="text"
                    color="error"
                    sx={{ mt: 2 }}
                  >
                    Delete
                  </Button>

                  <Button
                    onClick={() => {
                      setShowForm(true);
                      setEditId(item.id);
                      setDate(dayjs(item.date));
                      setSlots(item.time_slots);
                    }}
                    variant="text"
                    color="info"
                    sx={{ mt: 2, ml: 2 }}
                  >
                    Edit
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default BookSchedule;
